import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { WalkNoyauAnalysis } from '@/components/admin/sessions/walk/walk-noyau-analysis';
import { Fingerprint, NoyauAttempts } from '@/components/admin/sessions/walk/walk-noyau-attempts';
import {
  type AnalystReadingFacts,
  analystReadingFacts,
  trialReadingFacts,
} from '@/lib/analyst-reading';
import {
  type InstrumentFacts,
  appelsWord,
  callsByPass,
  hasReasoning,
  instrumentFacts,
  isDirectReading,
  isStyleAbsent,
  promptHeading,
  rankedAttempts,
  trialInstrumentFacts,
} from '@/lib/session-analysis';
import { fetchNoyauPrompts } from '@/lib/session-api';
import { costLabel } from '@/lib/session-labels';
import {
  DETERMINATION_STATE_LABEL,
  TRIAL_STATE_LABEL,
  attemptsWord,
  determinationDurationMs,
  determinationSentence,
  determinationTone,
  drawSentence,
  renderedModel,
  secondsLabel,
  trialSentence,
  trialSourceMatter,
} from '@/lib/session-noyau';
import {
  type NoyauAnalystPrompt,
  type NoyauDetermination,
  type NoyauTrial,
  type NoyauTrialSource,
  type PlayedProtocol,
  noyauOf,
  sortedNoyauScores,
} from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';

/**
 * The block « Détermination du noyau » of the parcours (noyau.md §9.1, issue
 * #152; maquette `SessionParcours`) - after the protocol's, bordered like an
 * ordinary block: the bleu nuit border is the protocol's alone.
 *
 * ## The analyst in TWO PASSES shows ALL its reasoning (analyste.md §11.1, #196)
 *
 * Maquettes `ParcoursAvantApres` and `ParcoursEtats`. Under the line of facts - the
 * seat, the model, the time, the cost and the CALLS, counted by pass: « 3 appels
 * (passe 1, passe 2 relancée) » - comes the line of the INSTRUMENT, under its violet
 * rule: the protocol and its version (a link to the section of THAT version), the
 * version of Méthode (a link to THAT version), `prompt 3` and the fingerprint of the
 * instrument, the two systems together (K11). Then the sections of `walk-noyau-analysis.tsx`: the first
 * pass, the levels, the elimination, the second pass and the six notes.
 *
 * What a determination of this kind says in its header: `style absent` (the second
 * pass failed twice, §7.5) beside the state. And what it says when it failed WITHOUT
 * a call - a session started before #196, which froze no complete instrument - is
 * the REASON the analysis stores (`analysis.failure`), never a guess.
 *
 * ## A determination of before #196 is drawn as it was
 *
 * No analysis and calls with no pass: the block stays what the rest of this comment
 * describes - the six notes with their sentences, « essais », the line « Lecture : »
 * of #154 - under the grey pill « lecture directe, avant #196 ». The pill belongs to
 * what has calls and no pass: a determination that read nothing - an old
 * `no-extracts` - is not « read directly ».
 *
 * ## What it says, drawn as before (the direct reading)
 *
 * The state, the seat and the model the API answered, the time from the close
 * of the protocol to the settlement, the cost and the attempts; then the SIX
 * notes, sorted, each with the colour of its noyau, its name, its note and its
 * sentence - the principal and the second marked. On `no-extracts` or `failed`
 * the sentence that says the sequence was skipped, and for a failure the cause
 * of EACH attempt, read again the way the Brut layer reads a scribe's answer.
 *
 * ## What it does not recount
 *
 * The cost and the tokens are in the eight tiles already, counted once by
 * `sessionFigures` (#149): the euro printed here is the determination's own,
 * the same column, and nothing adds it a second time.
 *
 * ## The served entry is read on a CLICK (§8)
 *
 * What the analyst was sent carries the matter of the whole session a second
 * time: it is behind its own route, mounted only when somebody asks - the
 * régime of the Brut layer of a turn. Since #196 there is one entry per CALL, each
 * marked with its pass, and the button says which passes it holds.
 *
 * ## Which instrument it was: the reading, the prompt, the fingerprint (#154)
 *
 * A line of facts of its own under the first one (§9.1, maquette
 * `LectureSessionParcours`): « Lecture : Entretien fondateur v4 · prompt 2 ·
 * empreinte 3f9a2c1e ». The reading PLAYED - the protocol and its version the
 * session froze, a link to the reading of THAT version (`?version=` and
 * `?vue=lecture`), never to today's -, the version of the assembly written in
 * code, and the fingerprint of the system served (N39) in fixed pitch, what
 * the measure of §13 groups sessions by. The prompt and the fingerprint are
 * the LAST call's (N40); a failure details each attempt, and each attempt
 * then carries its own.
 *
 * Two cases the spec leaves to this screen, decided by lot 7:
 *
 *   - a session started BEFORE #154 - its frozen protocol has no reading - was
 *     served the generic one (N38): the line says « Lecture : générique », with
 *     no link, the protocol's version holding nothing to read;
 *   - a call recorded before the fingerprint existed reads with a NULL one: the
 *     line says « sans empreinte » - a fact, where a dash or an omitted field
 *     would look like a value that failed to load - and keeps its prompt
 *     version, `1` for those calls, which is what tells them apart.
 *
 * No line at all without a call - a determination still `pending`, or one
 * settled `no-extracts` - : nothing was served, and a line naming an
 * instrument no call received would describe an event that did not happen.
 * The violet rule in the margin is the maquette's: violet is the reading's ink
 * in the editor too.
 *
 * That line - and « générique » with it - is the direct reading's. The line of the
 * analyst in two passes is built from the analysis (`instrumentFacts`): the
 * reading of before #196 is empty on every protocol born since the section exists,
 * and the helper that reads it would call each new determination « générique ».
 *
 * ## What a trial READ: the line « Matière : » (relecture.md §11, #187)
 *
 * A trial reads a SOURCE on the targets of the rangement - the matter of the
 * session, a blank rangement of it, or its review - and the scribe elsewhere.
 * The block says which, in a line under the reading's, inside the same violet
 * rule: « Matière : la relecture de la session, état du 12 oct. 16:31 (10
 * extraits), et le scribe ailleurs ». It shows even while the trial runs, and
 * with no call yet: the source is the press's, not the answer's. A trial kept
 * before the sources existed reads « la matière de la session ».
 *
 * ## The same block for a TRIAL of the analyst (#164)
 *
 * « Lancer l'analyste » (noyau.md §9.4) plays the analyst on the matter of the
 * session again, with the reading of the protocol's DEVELOPMENT version, and
 * writes no determination: a settled trial is kept as a trial, with its session
 * (#167). Its answer has the shape of a determination, so it is drawn
 * by this block under its own heading, and the block, its six rows and the
 * button of its served entry answer marks of their own: the determination and
 * a trial can share one screen. The lines INSIDE the block - the attempts, the
 * reading, the fingerprint, the entries served - keep the determination's
 * marks, and are found within their block. Three differences more: the
 * line « Lecture » names the development version it was served, the sentence
 * never speaks of a sequence being skipped (a trial skips nothing), and what the
 * analyst was served comes with the trial it was read with, rather than from
 * the determination's table.
 */

export interface WalkNoyauBlockProps {
  readonly sessionId: string;
  readonly determination: NoyauDetermination;
  /** The protocol the session FROZE, whose reading the analyst was served - null when it does not read back. */
  readonly protocol: PlayedProtocol | null;
  /** A trial of the analyst drawn by this block, `determination` being its own (see the head of this file). */
  readonly trial?: NoyauTrial | undefined;
}

export function WalkNoyauBlock({ sessionId, determination, protocol, trial }: WalkNoyauBlockProps) {
  const [served, setServed] = useState(false);
  const { analysis, calls } = determination;
  // The reason a determination settled `failed` with no call at all (a session started before #196).
  const failure =
    determination.state === 'failed' && calls.length === 0 ? (analysis?.failure ?? null) : null;
  const direct = isDirectReading(determination);
  const sentence =
    trial === undefined
      ? determinationSentence(determination)
      : trialSentence(determination, trial.source);
  const model = renderedModel(determination);
  const duration = determinationDurationMs(determination);
  const scoreTestId =
    trial === undefined ? SESSIONS_TEST_IDS.walkNoyauScore : SESSIONS_TEST_IDS.noyauTrialScore;

  // The line of the instrument: the analysis' own, never the reading of before.
  const instrument =
    analysis === null
      ? null
      : trial === undefined
        ? instrumentFacts(analysis, protocol, calls)
        : trialInstrumentFacts(trial, analysis);
  const reading =
    analysis !== null
      ? null
      : trial === undefined
        ? analystReadingFacts(protocol, calls)
        : trialReadingFacts(trial);
  const passes = callsByPass(calls);
  // The attempts of a failure; a style absent draws the second pass's own, inside its yellow line.
  const attempts =
    analysis === null
      ? determination.state === 'rendered'
        ? []
        : rankedAttempts(calls)
      : determination.state === 'failed'
        ? rankedAttempts(calls)
        : [];

  return (
    <article
      data-testid={
        trial === undefined ? SESSIONS_TEST_IDS.walkNoyau : SESSIONS_TEST_IDS.noyauTrialBlock
      }
      data-state={determination.state}
      data-analysis={analysis !== null ? 'two-passes' : direct ? 'direct' : undefined}
      className="overflow-hidden rounded-md border border-gray-200 bg-white"
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-gray-100 border-b bg-gray-50 px-4 py-2.5">
        <span className="font-bold text-gray-500 text-micro uppercase tracking-wide">
          {trial === undefined ? 'Bloc' : 'Essai'}
        </span>
        <h3 className="font-bold text-gray-900 text-sm">
          {trial === undefined ? 'Détermination du noyau' : 'Lecture à blanc de l’analyste'}
        </h3>
        <span className="ml-auto inline-flex flex-wrap items-center gap-1.5">
          <StatusBadge tone={determinationTone(determination.state)}>
            {
              (trial === undefined ? DETERMINATION_STATE_LABEL : TRIAL_STATE_LABEL)[
                determination.state
              ]
            }
          </StatusBadge>
          {isStyleAbsent(analysis) ? <StatusBadge tone="warning">style absent</StatusBadge> : null}
          {direct ? (
            <StatusBadge tone="neutral" data-testid={SESSIONS_TEST_IDS.walkNoyauBefore}>
              lecture directe, avant #196
            </StatusBadge>
          ) : null}
        </span>
      </header>

      <div className="flex flex-col gap-3.5 px-4 py-3">
        <div className="flex flex-col gap-2">
          <p className="flex flex-wrap gap-x-5 gap-y-2 text-caption text-gray-600">
            <span>
              Siège <b className="font-semibold text-gray-900">Analyste</b>
            </span>
            {model === null ? null : (
              <span>
                Modèle <b className="font-semibold text-gray-900">{model}</b>
              </span>
            )}
            {calls.length === 0 ? (
              // On `pending` a call is UNDER WAY: the calls are written with the
              // settlement (`settleNoyauDetermination`), so none is listed yet.
              <span>{determination.state === 'pending' ? 'Appel en cours' : 'Aucun appel'}</span>
            ) : (
              <>
                {duration === null ? null : (
                  <span>
                    <b className="font-bold text-gray-900 tabular-nums">
                      {secondsLabel(duration, analysis !== null)}
                    </b>
                  </span>
                )}
                <span>
                  <b className="font-bold text-gray-900 tabular-nums">
                    {costLabel(determination.costEur)}
                  </b>
                </span>
                <span>
                  <b className="font-bold text-gray-900 tabular-nums">{calls.length}</b>{' '}
                  {analysis === null
                    ? attemptsWord(calls.length)
                    : `${appelsWord(calls.length)}${passes === '' ? '' : ` (${passes})`}`}
                </span>
              </>
            )}
          </p>
          {trial !== undefined ? (
            <TrialFacts source={trial.source}>
              {instrument !== null ? (
                <InstrumentLine facts={instrument} bar={false} />
              ) : reading === null ? null : (
                <ReadingFacts facts={reading} bar={false} />
              )}
            </TrialFacts>
          ) : instrument !== null ? (
            <InstrumentLine facts={instrument} />
          ) : reading === null ? null : (
            <ReadingFacts facts={reading} />
          )}
        </div>

        {sentence === null ? null : (
          <p
            data-testid={failure === null ? undefined : SESSIONS_TEST_IDS.walkNoyauReason}
            data-reason={failure?.reason}
            className="text-caption text-gray-700 leading-normal"
          >
            {sentence}
          </p>
        )}

        {analysis !== null && hasReasoning(determination) ? (
          <WalkNoyauAnalysis
            determination={determination}
            analysis={analysis}
            scoreTestId={scoreTestId}
          />
        ) : (
          <DirectNotes determination={determination} scoreTestId={scoreTestId} />
        )}

        <NoyauAttempts attempts={attempts} />

        {calls.length === 0 ? null : served ? (
          trial === undefined ? (
            <NoyauServedPrompts sessionId={sessionId} />
          ) : (
            <NoyauPromptsList prompts={trial.prompts} />
          )
        ) : (
          <p className="flex flex-wrap items-center gap-3 text-caption">
            <button
              type="button"
              data-testid={
                trial === undefined
                  ? SESSIONS_TEST_IDS.walkNoyauPrompts
                  : SESSIONS_TEST_IDS.noyauTrialPrompts
              }
              onClick={() => setServed(true)}
              className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {analysis === null
                ? 'Voir l’entrée servie à l’analyste'
                : passes === ''
                  ? 'Voir les entrées servies'
                  : `Voir les entrées servies (${passes})`}
            </button>
            {trial === undefined ? (
              <span className="text-gray-500 text-xs">
                {analysis === null ? 'chargée' : 'chargées'} à la demande, table{' '}
                <code className="font-mono">session_noyau_prompts</code>
              </span>
            ) : (
              <span className="text-gray-500 text-xs">
                {analysis === null ? 'gardée' : 'gardées'} avec l’essai
              </span>
            )}
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * The six notes of a determination read directly (before #196), sorted, each with
 * the colour of its noyau, its name, its note and its sentence - the principal and
 * the second marked - and the tie a draw settled. Nothing when no note was given.
 * A determination of the new analyst whose reasoning is not there to draw (a
 * rendered one with no level, which the engine never writes) lands here too: its
 * six notes are still the truth.
 */
function DirectNotes({
  determination,
  scoreTestId,
}: {
  determination: NoyauDetermination;
  scoreTestId: string;
}) {
  // The order of the markdown and of `get_session`: a tie keeps the ranking's.
  const scores = sortedNoyauScores(determination);
  if (scores.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <p className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
        Les six notes, triées
      </p>
      <table className="w-full border-collapse text-caption">
        <caption className="sr-only">
          Les six notes de l’analyste, de la meilleure à la moins bonne
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-600">
            <th scope="col" className="py-1.5 pr-3 font-semibold uppercase tracking-wide">
              Noyau
            </th>
            <th scope="col" className="py-1.5 pr-3 font-semibold uppercase tracking-wide">
              Note
            </th>
            <th scope="col" className="py-1.5 font-semibold uppercase tracking-wide">
              Justification
            </th>
          </tr>
        </thead>
        <tbody>
          {scores.map((score) => {
            const noyau = noyauOf(score.noyau);
            const place =
              score.noyau === determination.principal
                ? 'principal'
                : score.noyau === determination.second
                  ? 'second'
                  : null;
            return (
              <tr
                key={score.noyau}
                data-testid={scoreTestId}
                data-noyau={score.noyau}
                data-place={place ?? undefined}
                className="border-gray-100 border-b align-top last:border-b-0"
              >
                <td className="whitespace-nowrap py-2.5 pr-3">
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: noyau.color }}
                    />
                    <span className="font-semibold text-gray-900 text-sm">{noyau.name}</span>
                    <span className="text-gray-500 text-micro">{score.noyau}</span>
                    {place === null ? null : (
                      <StatusBadge tone={place === 'principal' ? 'accent' : 'neutral'}>
                        {place}
                      </StatusBadge>
                    )}
                  </span>
                </td>
                <td className="whitespace-nowrap py-2.5 pr-3">
                  <span className="inline-flex items-center gap-2">
                    <b className="w-7 font-bold text-gray-900 text-sm tabular-nums">
                      {score.score}
                    </b>
                    <span
                      aria-hidden="true"
                      className="inline-block h-1.5 w-24 overflow-hidden rounded-pill bg-gray-100"
                    >
                      <span
                        className="block h-full rounded-pill"
                        style={{ width: `${score.score}%`, backgroundColor: noyau.color }}
                      />
                    </span>
                  </span>
                </td>
                <td className="py-2.5 text-gray-700">{score.justification}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {determination.draw === null ? null : (
        <p className="text-caption text-gray-600">{drawSentence(determination.draw)}</p>
      )}
    </div>
  );
}

/** The violet rule of the maquette, at the block's inner edge. */
function VioletRule() {
  return (
    <span
      aria-hidden="true"
      className="-left-4 absolute inset-y-0 w-[3px] rounded-sm bg-violet-deep"
    />
  );
}

/**
 * The facts of a TRIAL (relecture.md §11): the line of the instrument - or of the
 * reading of a trial of before #196 -, when a call answered, and the line « Matière : »
 * - the two under ONE violet rule, as the maquette `retouches` draws them.
 */
function TrialFacts({
  source,
  children,
}: {
  source: NoyauTrialSource;
  children: ReactNode;
}) {
  return (
    <div className="relative flex flex-col gap-1.5">
      <VioletRule />
      {children}
      <p
        data-testid={SESSIONS_TEST_IDS.noyauTrialMatter}
        data-source={source.kind}
        className="text-caption text-gray-600"
      >
        <b className="font-semibold text-gray-900">Matière :</b> {trialSourceMatter(source)}
      </p>
    </div>
  );
}

/**
 * « Lecture : Scénario de succès v18 · Méthode v2 » then « prompt 3 · empreinte de
 * l'instrument 7c41d09a » (§11.1, maquette `ParcoursAvantApres`): the protocol the
 * session froze - a link to the section of THAT version -, the version of Méthode
 * it was read with - a link to THAT version in Réglages > Méthode -, the assembly
 * written in code and the fingerprint of the instrument. Whatever is not known is not
 * said: no protocol when the frozen one does not read back, no Méthode on a header
 * that holds none.
 */
function InstrumentLine({ facts, bar = true }: { facts: InstrumentFacts; bar?: boolean }) {
  const { protocol, method } = facts;
  return (
    <p
      data-testid={SESSIONS_TEST_IDS.walkNoyauReading}
      data-reading={protocol === null ? 'unknown' : 'protocol'}
      className={`${bar ? 'relative ' : ''}flex flex-wrap gap-x-5 gap-y-2 text-caption text-gray-600`}
    >
      {bar ? <VioletRule /> : null}
      {protocol === null && method === null ? null : (
        <span>
          {protocol === null ? null : (
            <>
              Lecture :{' '}
              <Link
                to="/admin/protocoles/$protocolId"
                params={{ protocolId: protocol.protocolId }}
                search={{ version: protocol.versionId, vue: 'lecture' }}
                data-testid={SESSIONS_TEST_IDS.walkNoyauReadingLink}
                className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {protocol.name} v{protocol.versionNumber}
              </Link>
            </>
          )}
          {protocol !== null && method !== null ? ' · ' : null}
          {method === null ? null : (
            // The version the session froze, read-only in the tab where Méthode is written:
            // `?version=` is the régime of every editor, and it is THAT version - never today's.
            <Link
              to="/admin/reglages/methode"
              search={{ version: method.versionId }}
              data-testid={SESSIONS_TEST_IDS.walkNoyauMethod}
              data-version-id={method.versionId}
              className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Méthode v{method.number}
            </Link>
          )}
        </span>
      )}
      <span>
        {facts.promptVersion === null ? null : (
          <>
            prompt <b className="font-semibold text-gray-900 tabular-nums">{facts.promptVersion}</b>
            {' · '}
          </>
        )}
        <Fingerprint value={facts.fingerprint} label="empreinte de l’instrument" />
      </span>
    </p>
  );
}

/** « Lecture : <protocole> v<N> · prompt <version> · empreinte <8 hex> » (§9.1, N40) - the direct reading's line. */
function ReadingFacts({ facts, bar = true }: { facts: AnalystReadingFacts; bar?: boolean }) {
  const { reading } = facts;
  return (
    <p
      data-testid={SESSIONS_TEST_IDS.walkNoyauReading}
      data-reading={reading?.kind ?? 'unknown'}
      className={`${bar ? 'relative ' : ''}flex flex-wrap gap-x-5 gap-y-2 text-caption text-gray-600`}
    >
      {bar ? <VioletRule /> : null}
      {reading === null ? null : (
        <span>
          Lecture :{' '}
          {reading.kind === 'generic' ? (
            <b className="font-semibold text-gray-900">générique</b>
          ) : (
            <Link
              to="/admin/protocoles/$protocolId"
              params={{ protocolId: reading.protocolId }}
              search={{ version: reading.versionId, vue: 'lecture' }}
              data-testid={SESSIONS_TEST_IDS.walkNoyauReadingLink}
              className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {reading.name} v{reading.versionNumber}
            </Link>
          )}
        </span>
      )}
      <span>
        {facts.promptVersion === null ? null : (
          <>
            prompt <b className="font-semibold text-gray-900 tabular-nums">{facts.promptVersion}</b>
            {' · '}
          </>
        )}
        <Fingerprint value={facts.fingerprint} />
      </span>
    </p>
  );
}

/**
 * What the analyst was served, call by call, read when this component MOUNTS -
 * the régime of the served inputs of a turn (`layer-brut.tsx`).
 */
function NoyauServedPrompts({ sessionId }: { sessionId: string }) {
  const served = useQuery({
    queryKey: ['session-noyau-prompts', sessionId],
    queryFn: () => fetchNoyauPrompts(sessionId),
    // Written once with the settlement (§8): a re-read could never answer anything new.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (served.isPending) {
    return <p className="text-caption text-gray-600">Lecture de l’entrée servie…</p>;
  }
  if (served.isError || served.data === undefined) {
    return (
      <p role="alert" className="text-caption text-rouge">
        L’entrée servie n’a pas pu être lue. Elle est purgée avec la session (§12.4).
      </p>
    );
  }
  return <NoyauPromptsList prompts={served.data.prompts} />;
}

/**
 * What the analyst was served, call by call - read from the base, or held with a
 * trial. Each entry is titled by its pass and its rank inside it (« passe 2 · essai 2 »):
 * a retry is an entry of its own, the correction it was sent with included.
 */
function NoyauPromptsList({ prompts }: { prompts: readonly NoyauAnalystPrompt[] }) {
  if (prompts.length === 0) {
    return <p className="text-caption text-gray-600">Aucune entrée servie n’a été conservée.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {prompts.map((prompt, index) => (
        <section
          key={`${prompt.seat}-${index}`}
          data-testid={SESSIONS_TEST_IDS.walkNoyauPrompt}
          data-pass={prompt.pass ?? undefined}
          className="flex flex-col gap-1.5"
        >
          <h4 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
            {promptHeading(prompts, index)}
          </h4>
          <pre className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900 text-xs leading-5">
            {prompt.system}
          </pre>
          {prompt.messages.map((message, rank) => (
            <pre
              key={`${message.role}-${rank}`}
              className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-white p-3 font-mono text-gray-900 text-xs leading-5"
            >
              {message.role === 'user' ? '[user] ' : '[assistant] '}
              {message.content}
            </pre>
          ))}
        </section>
      ))}
    </div>
  );
}
